import { get, set, del } from "idb-keyval"
import { isOfflineFirstEnabled } from "@/lib/feature-flags"
import {
  getDeviceId,
  patchSyncMeta,
  recordConflict,
  reasonFromHttpStatus,
  type EntityType,
  type OfflineOpActor,
  type OfflineOpType,
  type OperationEnvelope,
  type OpAction,
} from "@/lib/offline"
import { errText } from "@/lib/errors"

/**
 * Offline Mutation Queue (Phase 1 envelope-aware)
 *
 * Stores mutations when offline and replays them when back online.
 * Critical for dinner service - orders must work without WiFi.
 *
 * Features:
 * - IndexedDB persistence (survives app restart)
 * - Operation envelope (opId, entityType, actor, deviceId, baseVersion)
 * - Queue size limit (max 50)
 * - Deduplication by payload within 5s
 * - Exponential backoff on retry
 * - Conflict recording for terminal 409/403
 * - Sync lock to prevent concurrent sync runs
 *
 * @see docs/OFFLINE_FIRST_DESIGN.md
 */

/** @deprecated Prefer OperationEnvelope — kept for existing callers/tests. */
export interface QueuedMutation {
  id: string
  type: OfflineOpType
  payload: Record<string, unknown>
  timestamp: number
  retryCount: number
  endpoint: string
  method: "POST" | "PATCH" | "PUT" | "DELETE"
  // Envelope fields (optional on legacy rows)
  opId?: string
  entityType?: EntityType
  entityId?: string
  action?: OpAction
  actor?: OfflineOpActor
  deviceId?: string
  clientTs?: number
  baseVersion?: string | null
}

export interface SyncResult {
  success: boolean
  mutationId: string
  error?: string
  /**
   * True when the mutation has been removed from the queue (either succeeded
   * or failed permanently — 409 conflict, max retries exhausted). False when
   * the mutation is still queued for retry.
   */
  terminal: boolean
}

export const QUEUE_KEY = "belvedere-offline-queue"
export const LAST_SYNC_KEY = "belvedere-last-sync-time"
export const SYNC_LOCK_KEY = "belvedere-sync-lock"
const MAX_RETRIES = 3
const MAX_QUEUE_SIZE = 50

const ANONYMOUS_ACTOR: OfflineOpActor = {
  userId: "unknown",
  staffId: null,
  role: null,
}

function newOpId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function defaultEntityType(type: OfflineOpType): EntityType {
  switch (type) {
    case "create_order":
    case "update_order_status":
      return "order"
    case "update_menu_availability":
      return "menu_item"
    case "create_reservation":
      return "reservation"
    default:
      return "order"
  }
}

function defaultAction(type: OfflineOpType): OpAction {
  switch (type) {
    case "create_order":
    case "create_reservation":
      return "batch_create"
    case "update_order_status":
    case "update_menu_availability":
      return "update"
    default:
      return "create"
  }
}

/** Normalize legacy queue rows into full envelopes. */
export function toEnvelope(row: QueuedMutation): OperationEnvelope {
  const opId = row.opId ?? row.id
  return {
    opId,
    entityType: row.entityType ?? defaultEntityType(row.type),
    entityId: row.entityId ?? String(row.payload.entityId ?? row.payload.tableId ?? opId),
    action: row.action ?? defaultAction(row.type),
    payload: row.payload,
    actor: row.actor ?? ANONYMOUS_ACTOR,
    deviceId: row.deviceId ?? "unknown",
    clientTs: row.clientTs ?? row.timestamp,
    baseVersion: row.baseVersion ?? null,
    endpoint: row.endpoint,
    method: row.method,
    retryCount: row.retryCount,
    type: row.type,
  }
}

function fromEnvelope(env: OperationEnvelope): QueuedMutation {
  return {
    id: env.opId,
    opId: env.opId,
    type: env.type,
    payload: env.payload,
    timestamp: env.clientTs,
    retryCount: env.retryCount,
    endpoint: env.endpoint,
    method: env.method,
    entityType: env.entityType,
    entityId: env.entityId,
    action: env.action,
    actor: env.actor,
    deviceId: env.deviceId,
    clientTs: env.clientTs,
    baseVersion: env.baseVersion,
  }
}

/**
 * Get all queued mutations
 */
export async function getQueuedMutations(): Promise<QueuedMutation[]> {
  const queue = await get<QueuedMutation[]>(QUEUE_KEY)
  return queue || []
}

export type QueueMutationInput = {
  type: OfflineOpType
  payload: Record<string, unknown>
  endpoint: string
  method: "POST" | "PATCH" | "PUT" | "DELETE"
  entityType?: EntityType
  entityId?: string
  action?: OpAction
  actor?: OfflineOpActor
  baseVersion?: string | null
  /** If provided, used as opId (must be unique). */
  opId?: string
}

/**
 * Add a mutation to the offline queue.
 * Enforces MAX_QUEUE_SIZE, feature flag, and prevents duplicate IDs/payloads.
 */
export async function queueMutation(
  mutation: QueueMutationInput | Omit<QueuedMutation, "id" | "timestamp" | "retryCount">,
): Promise<QueuedMutation> {
  if (!isOfflineFirstEnabled()) {
    throw new Error(
      "Offline capture is disabled. Reconnect or enable NEXT_PUBLIC_ENABLE_OFFLINE_FIRST.",
    )
  }

  const queue = await getQueuedMutations()

  if (queue.length >= MAX_QUEUE_SIZE) {
    throw new Error(
      `Offline queue is full (${MAX_QUEUE_SIZE} items). Please connect to sync before adding more orders.`,
    )
  }

  const deviceId = await getDeviceId()
  const opId = ("opId" in mutation && mutation.opId) || newOpId()
  const clientTs = Date.now()
  const type = mutation.type
  const envelope: OperationEnvelope = {
    opId,
    entityType: mutation.entityType ?? defaultEntityType(type),
    entityId:
      mutation.entityId ??
      String(mutation.payload.entityId ?? mutation.payload.tableId ?? opId),
    action: mutation.action ?? defaultAction(type),
    payload: mutation.payload,
    actor: mutation.actor ?? ANONYMOUS_ACTOR,
    deviceId,
    clientTs,
    baseVersion: mutation.baseVersion ?? null,
    endpoint: mutation.endpoint,
    method: mutation.method,
    retryCount: 0,
    type,
  }

  const newMutation = fromEnvelope(envelope)

  // Deduplication: identical type+endpoint+payload within last 5 seconds
  const fiveSecondsAgo = Date.now() - 5000
  const isDuplicate = queue.some(
    (m) =>
      m.type === newMutation.type &&
      m.endpoint === newMutation.endpoint &&
      m.timestamp > fiveSecondsAgo &&
      JSON.stringify(m.payload) === JSON.stringify(newMutation.payload),
  )

  if (isDuplicate) {
    const existing = queue.find(
      (m) =>
        m.type === newMutation.type &&
        m.endpoint === newMutation.endpoint &&
        m.timestamp > fiveSecondsAgo &&
        JSON.stringify(m.payload) === JSON.stringify(newMutation.payload),
    )!
    return existing
  }

  queue.push(newMutation)
  await set(QUEUE_KEY, queue)

  return newMutation
}

/**
 * Remove a mutation from the queue (after successful sync)
 */
export async function removeMutation(mutationId: string): Promise<void> {
  const queue = await getQueuedMutations()
  const filtered = queue.filter((m) => m.id !== mutationId && m.opId !== mutationId)
  await set(QUEUE_KEY, filtered)
}

/**
 * Update a mutation's retry count
 */
export async function incrementRetryCount(mutationId: string): Promise<void> {
  const queue = await getQueuedMutations()
  const updated = queue.map((m) =>
    m.id === mutationId || m.opId === mutationId
      ? { ...m, retryCount: m.retryCount + 1 }
      : m,
  )
  await set(QUEUE_KEY, updated)
}

/**
 * Clear all queued mutations (use with caution)
 */
export async function clearQueue(): Promise<void> {
  await del(QUEUE_KEY)
}

/**
 * Get queue size for UI display
 */
export async function getQueueSize(): Promise<number> {
  const queue = await getQueuedMutations()
  return queue.length
}

/**
 * Get the last sync timestamp (ms since epoch), or null if never synced.
 */
export async function getLastSyncTime(): Promise<number | null> {
  const ts = await get<number>(LAST_SYNC_KEY)
  return ts ?? null
}

/**
 * Set the last sync timestamp.
 */
async function setLastSyncTime(): Promise<void> {
  await set(LAST_SYNC_KEY, Date.now())
  await patchSyncMeta({ lastSyncAt: Date.now() })
}

/**
 * Acquire a sync lock to prevent concurrent sync runs.
 * Returns true if lock acquired, false if another sync is in progress.
 * Lock auto-expires after 60 seconds (guard against stale locks from crashes).
 */
async function acquireSyncLock(): Promise<boolean> {
  const lockTime = await get<number>(SYNC_LOCK_KEY)
  const now = Date.now()

  if (lockTime && now - lockTime < 60000) {
    return false
  }

  await set(SYNC_LOCK_KEY, now)
  return true
}

/**
 * Release the sync lock.
 */
async function releaseSyncLock(): Promise<void> {
  await del(SYNC_LOCK_KEY)
}

/**
 * Execute a single mutation against the API
 */
/**
 * Endpoints whose payload may carry `occurredAt`.
 *
 * Deliberately a narrow allow-list. `/api/db` is NOT here: the proxy forwards
 * unknown keys onward, and writing a column that does not exist is a SILENT
 * reject in this stack (the row simply never appears), so nothing extra goes
 * near that payload without its own migration and proof. Offline reservations
 * therefore still replay as they always did.
 */
export const OCCURRED_AT_ENDPOINTS: readonly string[] = ["/api/orders/batch"]

/**
 * Stamp a replayed payload with the instant the work was actually done.
 *
 * The queue already records `timestamp` (the client clock at enqueue) and used
 * to discard it, so `app/api/orders/batch` resolved the dining day from the
 * REPLAY instant — filing Friday's dinner under Saturday whenever a tablet
 * reconnected the next morning. Injecting here, at the one funnel every replay
 * passes through, means payloads already sitting in a device's IndexedDB from
 * before this shipped are corrected too.
 *
 * The server validates and bounds this value (`resolveBusinessInstant`); it is
 * evidence, not an instruction.
 */
export function payloadWithOccurredAt(mutation: {
  endpoint: string
  timestamp: number
  payload: unknown
}): unknown {
  if (!OCCURRED_AT_ENDPOINTS.includes(mutation.endpoint)) return mutation.payload
  if (typeof mutation.payload !== "object" || mutation.payload === null) return mutation.payload
  if (Array.isArray(mutation.payload)) return mutation.payload

  const payload = mutation.payload as Record<string, unknown>
  if (payload.occurredAt != null) return payload

  return { ...payload, occurredAt: new Date(mutation.timestamp).toISOString() }
}

async function executeMutation(mutation: QueuedMutation): Promise<Response> {
  const response = await fetch(mutation.endpoint, {
    method: mutation.method,
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    // ⛔ DO NOT REMOVE. An expired session does NOT come back as 401 here.
    // `proxy.ts` 307s an unauthenticated POST /api/db to /login, and a 307
    // PRESERVES METHOD AND BODY -- so under the fetch default of
    // `redirect: "follow"` the replay lands on POST /login -> 200 and
    // `response.ok` is TRUE. syncQueue then deleted the order and the UI
    // toasted "Synced N orders successfully!". Proven live against production
    // 2026-08-20. "manual" turns that into an opaque redirect, which
    // `isAuthRedirect` routes to the same loud terminal path as a 401.
    redirect: "manual",
    // Carries the instant the order was TAKEN, not the instant it replayed.
    body: JSON.stringify(payloadWithOccurredAt(mutation)),
  })

  return response
}

/**
 * True when a replay response is really "your session is gone", not a success.
 *
 * With `redirect: "manual"` a middleware redirect surfaces as an opaque
 * redirect: `type === "opaqueredirect"`, `status === 0`, `ok === false`, and an
 * empty `url`. We also defensively catch a bare 3xx and a response whose final
 * URL is the login page, so this still holds if the fetch options are ever
 * changed back to following redirects.
 */
export function isAuthRedirect(
  response: Pick<Response, "type" | "status" | "url">,
): boolean {
  if (response.type === "opaqueredirect") return true
  if (response.status === 0) return true
  if (response.status >= 300 && response.status < 400) return true
  try {
    return new URL(response.url).pathname === "/login"
  } catch {
    return false
  }
}

/**
 * Calculate exponential backoff delay for a given retry count.
 * Returns delay in ms: 1s, 2s, 4s, 8s, capped at 30s.
 */
function getBackoffDelay(retryCount: number): number {
  return Math.min(1000 * Math.pow(2, retryCount), 30000)
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function handleTerminalFailure(
  mutation: QueuedMutation,
  status: number,
  errorMessage: string,
): Promise<void> {
  const env = toEnvelope(mutation)
  await recordConflict({
    opId: env.opId,
    entityType: env.entityType,
    entityId: env.entityId,
    reason: reasonFromHttpStatus(status),
    clientPayload: env.payload,
    httpStatus: status,
    errorMessage,
  })
  await removeMutation(mutation.id)
}

/**
 * Sync all queued mutations to the server.
 * Uses a lock to prevent concurrent sync runs (e.g., double-fire on reconnect).
 * Applies exponential backoff between failed retries.
 * Returns results for each mutation.
 */
export async function syncQueue(): Promise<SyncResult[]> {
  const locked = await acquireSyncLock()
  if (!locked) {
    return []
  }

  try {
    const queue = await getQueuedMutations()
    const results: SyncResult[] = []

    for (const mutation of queue) {
      if (mutation.retryCount > 0) {
        await delay(getBackoffDelay(mutation.retryCount))
      }

      try {
        const response = await executeMutation(mutation)

        if (isAuthRedirect(response)) {
          // Session expired mid-queue. Same loud terminal handling as a 401 --
          // conflict record + red toast -- instead of the silent delete that a
          // followed 307 used to produce.
          await handleTerminalFailure(
            mutation,
            401,
            "Session expired before this could be sent — sign in and re-enter it",
          )
          results.push({
            success: false,
            mutationId: mutation.id,
            error: "Session expired — order was NOT sent",
            terminal: true,
          })
        } else if (response.ok) {
          await removeMutation(mutation.id)
          await patchSyncMeta({ lastSuccessfulOpId: mutation.opId ?? mutation.id })
          results.push({ success: true, mutationId: mutation.id, terminal: true })
        } else if (response.status === 409) {
          // Conflict — remove; record for staff visibility
          await handleTerminalFailure(
            mutation,
            409,
            "Conflict: Data was modified while offline",
          )
          results.push({
            success: false,
            mutationId: mutation.id,
            error: "Conflict: Data was modified while offline",
            terminal: true,
          })
        } else if (response.status === 401 || response.status === 403) {
          await handleTerminalFailure(
            mutation,
            response.status,
            `Not allowed (${response.status}) — re-login and re-enter if needed`,
          )
          results.push({
            success: false,
            mutationId: mutation.id,
            error: `Permission/session error (${response.status})`,
            terminal: true,
          })
        } else if (mutation.retryCount < MAX_RETRIES) {
          await incrementRetryCount(mutation.id)
          results.push({
            success: false,
            mutationId: mutation.id,
            error: `Server error (${response.status}), will retry`,
            terminal: false,
          })
        } else {
          await handleTerminalFailure(
            mutation,
            response.status,
            `Failed after ${MAX_RETRIES} retries`,
          )
          results.push({
            success: false,
            mutationId: mutation.id,
            error: `Failed after ${MAX_RETRIES} retries`,
            terminal: true,
          })
        }
      } catch (error) {
        if (mutation.retryCount < MAX_RETRIES) {
          await incrementRetryCount(mutation.id)
        }
        results.push({
          success: false,
          mutationId: mutation.id,
          error: errText(error, "Network error"),
          terminal: false,
        })
        break
      }
    }

    await setLastSyncTime()

    return results
  } finally {
    await releaseSyncLock()
  }
}

/**
 * Check if we're online
 */
export function isOnline(): boolean {
  return typeof navigator !== "undefined" ? navigator.onLine : true
}

/**
 * Generate a temporary ID for offline-created records
 * These get replaced with real IDs after sync
 */
export function generateTempId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

/** Whether offline queueing is allowed for this action kind (Phase 1 matrix). */
export function isOfflineAllowedOp(type: OfflineOpType): boolean {
  if (!isOfflineFirstEnabled()) return false
  switch (type) {
    case "create_order":
    case "create_reservation":
    case "update_order_status":
    case "update_menu_availability":
      return true
    default:
      return false
  }
}
